<template>
  <div class="plugins">
    <Button class="top-create--btn" type="primary" @click="showCreateModal">{{ $t('plugins.createPlugin') }}</Button>
    <Table :loading="loading" :columns="recordsCol" :data="records"></Table>

    <!-- Create Plugin -->
    <Modal
      v-model="createModalVisible"
      class-name="plugins-modal--create"
      :title="$t('plugins.createPlugin')"
      :width="600"
      :mask-closable="false"
      @on-ok="save"
    >
      <Form v-if="createModalVisible" ref="createForm" :model="createForm" :rules="rules">
        <Row>
          <i-col v-if="warningMessage !== ''" span="24">
            <Alert type="warning" show-icon closable @on-close="handleWarnClose">{{ warningMessage }}</Alert>
          </i-col>
          <i-col span="24">
            <FormItem :label="$t('common.type')" prop="type">
              <Select v-model="createForm.type" size="large" @on-change="handleTypeChange">
                <Option v-for="item in typesList" :key="item" :value="item">{{ item }} </Option>
              </Select>
            </FormItem>
          </i-col>
          <i-col span="24" class="name">
            <FormItem :label="$t('common.name')" prop="name">
              <a
                v-show="pluginDocLink"
                :href="pluginDocLink"
                target="_blank"
                rel="noopener noreferrer"
                class="doc--link"
              >
                {{ $t('common.documentation') }}
              </a>
              <Select
                filterable
                allow-create
                v-model="createForm.name"
                size="large"
                :placeholder="$t('plugins.selectOrInput')"
                @on-change="handleNameChange"
                @on-create="handleCreateName"
                @on-open-change="handleSelectName"
              >
                <Option
                  v-for="(label, value) in availablePluginsMap"
                  :key="value"
                  :value="value"
                  :label="value"
                ></Option>
              </Select>
            </FormItem>
          </i-col>
          <i-col span="24" class="file">
            <FormItem :label="$t('plugins.file')" prop="file">
              <a href="javascript:void(0);" class="upload-file--link" @click="showUploadFileModal">
                {{ $t('upload.uploadFile') }}
              </a>
              <FileListAutoCompeleteSeletor
                ref="fileListSelectRef"
                v-model="createForm.file"
                :prefix="'file://'"
              ></FileListAutoCompeleteSeletor>
            </FormItem>
          </i-col>
          <i-col v-if="fileWarningMessage !== '' && isShowFileWaring" span="24">
            <Alert type="warning" show-icon closable @on-close="handleFileWarnClose">{{ fileWarningMessage }}</Alert>
          </i-col>

          <i-col span="24">
            <FormItem :label="$t('plugins.shellParas')" prop="shellParas" :rules="tdengineRules">
              <Tooltip max-width="250px" transfer :content="$t('plugins.shellParasDesc')" placement="top">
                <Icon type="md-help-circle"></Icon>
              </Tooltip>
              <Select
                v-model="createForm.shellParas"
                filterable
                multiple
                allow-create
                size="large"
                :placeholder="isTDengine ? $t('plugins.TDenginePlaceholder') : $t('rules.enterCreate')"
                @on-create="addParams"
              >
                <Option v-for="(value, index) in shellOptions" :value="value" :key="index">
                  {{ value }}
                </Option>
              </Select>
            </FormItem>
          </i-col>
          <!--  functions -->
          <i-col v-if="isShowFuncs" span="24">
            <FormItem :label="$t('plugins.functions')" prop="functions">
              <Tooltip max-width="250px" transfer :content="$t('plugins.functionsDesc')" placement="top">
                <Icon type="md-help-circle"></Icon>
              </Tooltip>
              <Select v-model="createForm.functions" filte rable multiple allow-create size="large">
                <Option v-for="(value, index) in functions" :value="value.name" :key="index.name">
                  {{ value.name }}
                </Option>
              </Select>
            </FormItem>
          </i-col>
        </Row>
      </Form>
      <div slot="footer">
        <Button type="text" @click="createModalVisible = false">{{ $t('common.cancel') }}</Button>
        <Button type="primary" :loading="btnLoading" @click="save">{{ $t('common.submit') }}</Button>
      </div>
    </Modal>

    <!-- View Plugin -->
    <Modal
      v-model="viewModalVisible"
      class-name="plugins-modal--view view-form"
      :title="$t('plugins.viewPlugin')"
      :width="500"
      :mask-closable="false"
    >
      <div class="view-item">
        <label class="view-item--label">{{ $t('common.name') }}: </label>
        <span>{{ viewForm.name }}</span>
      </div>
      <div class="view-item">
        <label class="view-item--label">{{ $t('common.version') }}: </label>
        <span>{{ viewForm.version }}</span>
      </div>
      <div slot="footer">
        <Button type="primary" @click="viewModalVisible = false">{{ $t('common.ok') }}</Button>
      </div>
    </Modal>

    <!-- Register Function -->
    <Modal
      v-model="showRegisterFuncVisisble"
      class-name="plugins-modal--view view-form"
      :title="$t('plugins.registerFunction')"
      :width="500"
      :mask-closable="false"
    >
      <Select
        v-model="registerFuncForm.functions"
        filterable
        multiple
        allow-create
        size="large"
        @on-create="handleCreateName"
      >
        <Option v-for="(value, index) in functions" :value="value.name" :key="index.name">
          {{ value.name }}
        </Option>
      </Select>
      <div slot="footer">
        <Button type="text" @click="showRegisterFuncVisisble = false">{{ $t('common.cancel') }}</Button>
        <Button type="primary" :loading="btnLoading" @click="registerFunctions">{{ $t('common.submit') }}</Button>
      </div>
    </Modal>

    <!-- Upload File -->
    <UploadFileModal
      :modalVisible.sync="uploadFileModalVisible"
      :isSupportCustom="true"
      :fileAccepts="'.zip'"
      @reload="getFileList"
    >
    </UploadFileModal>
  </div>
</template>

<script lang="jsx">
import { loadAllFunctions } from '@/api/rules'
import { createPlugin, loadPlugins, viewPlugin, deletePlugin, loadAvailablePlugins, registerFunc } from '@/api/plugins'
import nodeID from '@/mixins/nodeID'
import currentLang from '@/mixins/currentLang'
import FileListAutoCompeleteSeletor from '../../configuration/uploads/components/FileListAutoSelector.vue'
import UploadFileModal from '../../configuration/uploads/UploadFileModal.vue'
import { LANG_VALUES_MAP } from '@/utils/constants'

export default {
  name: 'Plugins',

  mixins: [nodeID, currentLang],

  components: { FileListAutoCompeleteSeletor, UploadFileModal },

  data() {
    return {
      loading: false,
      createModalVisible: false,
      viewModalVisible: false,
      showRegisterFuncVisisble: false,
      btnLoading: false,
      registerFuncForm: {
        name: '',
        functions: [],
      },
      warningMessage: '',
      fileWarningMessage: '',
      isShowFileWaring: false,
      viewForm: {},
      typesList: ['sources', 'sinks', 'functions'],
      availablePluginsMap: {},
      shellOptions: [''],
      functions: [],
      createForm: {
        type: '',
        name: '',
        file: '',
        shellParas: [],
        functions: [],
      },
      rules: {
        type: [{ required: true, message: this.$t('plugins.typeRequired') }],
        name: [{ required: true, message: this.$t('plugins.nameRequired') }],
        file: [{ required: true, message: this.$t('plugins.fileRequired') }],
      },
      records: [],
      recordsCol: [
        {
          title: this.$t('common.name'),
          key: 'name',
          render: (h, params) => {
            return (
              <div>
                <a
                  href="javascript:;"
                  onClick={() => {
                    this.showDetail(params.row)
                  }}
                >
                  {params.row.name}
                </a>
              </div>
            )
          },
        },
        {
          title: this.$t('common.type'),
          key: 'type',
        },
        {
          title: this.$t('common.operations'),
          key: 'action',
          width: 200,
          align: 'center',
          render: (h, params) => {
            const editFuncBtn = (
              <a
                href="javascript:;"
                style="margin-right: 10px;"
                vOn:click={() => {
                  this.showRegisterFunctions(params.row)
                }}
              >
                <Icon type="ios-create"></Icon>
              </a>
            )
            return (
              <div>
                {params.row.type === 'functions' ? editFuncBtn : null}
                <a href="javascript:;">
                  <Poptip
                    confirm={true}
                    transfer={true}
                    width={200}
                    title={this.$t('common.confirmDelete')}
                    placement="top"
                    type="error"
                    vOn:on-ok={() => {
                      this.remove(params.row)
                    }}
                    vOn:on-cancel={() => {}}
                  >
                    <Icon type="md-trash"></Icon>
                  </Poptip>
                </a>
              </div>
            )
          },
        },
      ],
      uploadFileModalVisible: false,
      pluginDocLink: '',
    }
  },

  watch: {
    createModalVisible(val) {
      if (!val) {
        this.$refs.createForm.resetFields()
        this.createForm = {
          shellParas: [],
          functions: [],
        }
        this.warningMessage = ''
        this.availablePluginsMap = {}
      }
    },
  },

  computed: {
    isTDengine() {
      return this.createForm.type === 'sinks' && this.createForm.name === 'tdengine'
    },
    tdengineRules() {
      return this.isTDengine
        ? [{ required: true, message: this.$t('plugins.shellParamsRequired') }]
        : [{ required: false }]
    },
    // only `image` and `geo` plugin support register Functions when create or edit plugin
    isShowFuncs() {
      const plugins = ['image', 'geo']
      return this.createForm.type === 'functions' && plugins.includes(this.createForm.name)
    },
  },

  created() {
    const { query } = this.$route
    const { fromType, pluginName } = JSON.parse(sessionStorage.getItem('installInfo')) || {}
    if (['sources', 'sinks'].includes(query.fromPageType) && fromType) {
      this.createForm.type = fromType
      this.createForm.name = pluginName
      this.getAvailablePlugins(fromType, pluginName)
      setTimeout(() => {
        this.createModalVisible = true
      }, 500)
    }
    sessionStorage.removeItem('installInfo')
    this.loadData()
  },

  methods: {
    loadData() {
      this.loading = true
      this.records = []
      Promise.all([
        loadPlugins(this.nodeID, 'sources'),
        loadPlugins(this.nodeID, 'sinks'),
        loadPlugins(this.nodeID, 'functions'),
      ])
        .then((res) => {
          const records = res.map((item) => {
            const { data } = item
            const urlArr = item.config.url.split('/')
            const type = urlArr[urlArr.length - 1]
            return {
              data,
              type,
            }
          })
          records.forEach((record) => {
            record.data.forEach((col) => {
              this.records.push({
                name: col,
                type: record.type,
              })
            })
          })
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },
    showDetail({ name, type }) {
      this.viewModalVisible = true
      viewPlugin(this.nodeID, name, type)
        .then((res) => {
          this.viewForm = res.data
        })
        .catch(() => {})
    },

    remove({ name, type }) {
      deletePlugin(this.nodeID, name, type)
        .then((res) => {
          if (res.status === 200 && res.statusText === 'OK') {
            this.$Modal.success({
              title: this.$t('common.deleteSuccess'),
              content: this.$t('plugins.deleteNotice'),
            })
          } else {
            this.$Message.success(this.$t('common.deleteSuccess'))
          }
        })
        .catch(() => {})
    },
    showCreateModal() {
      this.createModalVisible = true
    },

    handleTypeChange(type) {
      if (type) {
        this.getAvailablePlugins(type)
      }
      if (type === 'functions') {
        this.pluginDocLink = ''
      }

      this.setPluginDefaultFuncs()
      this.validateShellParas()
    },

    handleNameChange(name) {
      this.setPluginDefaultFuncs(name)
      this.validateShellParas()

      if (!name) {
        this.createForm.file = ''
        this.fileWarningMessage = ''
      } else {
        if (this.createForm.type !== 'functions') {
          // the api not support to get docs when type is functions
          this.getPluginDocLinkByName(this.createForm.type, name)
        }

        const file = this.availablePluginsMap[name]
        this.$set(this.createForm, 'file', file)
        this.fileWarningMessage = `
          ${this.$t('plugins.fileWarningMessage')}
          ${file.replace('amd', 'arm')}
        `
      }
    },
    handleSelectName(isSelect) {
      if (!this.createForm.type && isSelect) {
        this.$Message.warning(this.$t('plugins.typeFirst'))
      }
    },
    handleCreateName(name) {
      this.createForm.name = name
      this.availablePluginsMap[name] = ''
      this.$set(this.createForm, 'file', '')
    },

    validateShellParas() {
      this.$nextTick(() => {
        this.$refs.createForm.validateField('shellParas')
      })
    },
    addParams(val) {
      this.shellOptions.push(val)
    },

    showUploadFileModal() {
      this.uploadFileModalVisible = true
    },
    getFileList() {
      this.$refs.fileListSelectRef.getFileList()
    },

    // get functions data of plugin of `function` type
    loadPluginsMetaData(name) {
      return new Promise((resolve, reject) => {
        loadAllFunctions(this.nodeID)
          .then((res) => {
            const curItem = res.data.find((item) => item.name === name)
            this.functions = curItem?.functions || []
            resolve(this.functions)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
    setPluginDefaultFuncs(pluginName) {
      if (!pluginName) {
        this.createForm.functions = []
        return
      }

      if (this.isShowFuncs) {
        this.loadPluginsMetaData(pluginName).then((res) => {
          this.createForm.functions = res.map((item) => item.name)
        })
      } else {
        this.createForm.functions = []
      }
    },

    getPluginDocLinkByName(type, name) {
      const lang = LANG_VALUES_MAP.get(this.lang)[1]
      if (name && type) {
        const url = `https://github.com/lf-edge/ekuiper/blob/master/docs/${lang}/rules/${type}/plugin/${name}.md`
        this.pluginDocLink = url
        return
      }
      this.pluginDocLink = ''
    },

    getAvailablePlugins(type, name) {
      loadAvailablePlugins(this.nodeID, type)
        .then((res) => {
          this.availablePluginsMap = res.data
          if (name) {
            this.handleNameChange(name)
          }
          this.isShowFileWaring = false
        })
        .catch((error) => {
          this.warningMessage = error.response.data
          this.isShowFileWaring = false
          // if (error.response.data.includes('Cannot fetch plugin list from')) {
          //   this.isShowFileWaring = true
          //   this.warningMessage = ''
          //   const url = error.response.data.split('from ')[1].split(',')[0]
          //   switch (type) {
          //     case 'sources':
          //       this.availablePluginsMap = {
          //         random: `${url}/random_amd64.zip`,
          //         zmq: `${url}/zmq_amd64.zip`,
          //       }
          //       break
          //     case 'sinks':
          //       this.availablePluginsMap = {
          //         file: `${url}/file_amd64.zip`,
          //         image: `${url}/image_amd64.zip`,
          //         influx: `${url}/influx_amd64.zip`,
          //         tdengine: `${url}/tdengine_amd64.zip`,
          //         zmp: `${url}/zmp_amd64.zip`,
          //         redis: `${url}/redis_amd64.zip`,
          //       }
          //       break
          //     default:
          //       this.availablePluginsMap = {
          //         accumulateWordCount: `${url}/accumulateWordCount_amd64.zip`,
          //         countPlusOne: `${url}/countPlusOne_amd64.zip`,
          //         echo: `${url}/echo_amd64.zip`,
          //         image: `${url}/image_amd64.zip`,
          //         geohash: `${url}/geohash_amd64.zip`,
          //         labelImage: `${url}/labelImage_amd64.zip`,
          //       }
          //   }
          // } else {
          //   this.isShowFileWaring = false
          // }
        })
    },

    // handleCreateFunc(func) {
    //   this.registerForm.function.push(func)
    // },
    handleWarnClose() {
      this.warningMessage = ''
    },
    handleFileWarnClose() {
      this.fileWarningMessage = ''
    },

    save() {
      this.$refs.createForm.validate((valid) => {
        if (!valid) {
          return
        }
        this.btnLoading = true
        const data = { ...this.createForm }
        const { type, shellParas } = data
        data.shellParas = shellParas.filter((param) => param !== '')
        delete data.type
        createPlugin(this.nodeID, data, type)
          .then((res) => {
            if (res.status === 201 && res.statusText === 'Created') {
              this.$Message.success(res.data)
            } else {
              this.$Message.success(this.$t('common.addSuccess'))
            }
            this.loadData()
            this.btnLoading = false
            this.createModalVisible = false
          })
          .catch(() => {
            this.btnLoading = false
          })
      })
    },

    // Register Functions
    showRegisterFunctions({ name }) {
      this.showRegisterFuncVisisble = true
      this.registerFuncForm.name = name
      this.registerFuncForm.functions = []
      this.loadPluginsMetaData(name)
    },
    registerFunctions() {
      if (this.registerFuncForm.functions.length === 0) {
        this.$Message.warning(this.$t('plugins.functionsRequired'))
        return
      }
      const { name, functions } = this.registerFuncForm
      const data = { functions }
      registerFunc(this.nodeID, name, data).then((res) => {
        this.registerForm = {
          name: '',
          functions: [],
        }
        this.$Message.success(res.data)
        this.showRegisterFuncVisisble = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/mixins.scss';

.upload-file--link {
  float: right;
  color: var(--color-text-normal);
}
.doc--link {
  float: right;
}
::v-deep .file .ivu-form-item-content,
::v-deep .name .ivu-form-item-content {
  @include clearfix();
}
</style>
